import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Avatar,
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  FAIconName,
  FireflyButton,
  Icon,
  InputGroupV2,
  Text,
  TextInputV2,
  UniversalSelect,
} from '@equip.health/ui';
import { debounce } from 'lodash';

import {
  ProfileEventsAdmin,
  ProfileEventsMaud,
} from '~/analytics/profileEvents';
import { useProvidersSearchData } from '~/components/maud/inquiries/InquiryDetail/useProvidersSearchData';
import { getMenuOptions } from '~/components/maud/inquiries/InquiryDetail/viewUtil';
import { validateEmail } from '~/lib/common';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import {
  ERROR_MESSAGES,
  credentialsEnumMenuOptionsMapper,
  enforceMinMaxBoundsOnHoursPerWeek,
  getMultiSelectedUniversalSelectOptions,
  isCharacterCountExceeded,
  isNPIValid,
  isPhoneNumberValid,
  providerEnumMenuOptionsMapper,
  providerLanguagesEnumMenuOptionsMapper,
  providerStatesEnumMenuOptionsMapper,
  providerTimezoneEnumMenuOptionsMapper,
} from '~/lib/util/profile.util';
import {
  useGetProviderCapacityEnumQuery,
  useGetProviderCredentialsEnumQuery,
  useGetProviderLanguagesEnumQuery,
  useGetProviderPronounsEnumQuery,
  useGetProviderStatesEnumQuery,
  useGetProviderTimezonesEnumQuery,
  useGetProviderTypesEnumQuery,
} from '~/store/services/enumerations';
import AvatarUpload from './AvatarUpload';
import { USER_STATUSES } from '~/lib/constants/profile.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

type SelectChangeHandler = (
  field: keyof EditProfile,
) => (selectedOptions: UniversalSelectOption[]) => void;

type FieldChangeHandler = (
  field: keyof EditProfile,
  value: string | string[],
) => void;

export interface AddEditProfileFormProps {
  editForm: EditProfile;
  handleChange: FieldChangeHandler;
  handleSelectChange: SelectChangeHandler;
  requiredFieldErrors?: RequiredFieldErrors;
  showDisplayName?: boolean;
  handleDisplayButtonClick?: (value: boolean) => void;
  supervisorFullName?: string;
  isAddForm?: boolean;
  onDeleteAvatar?: () => void;
}

const AddEditProfileForm = ({
  editForm,
  handleChange,
  handleSelectChange,
  requiredFieldErrors,
  showDisplayName,
  supervisorFullName,
  isAddForm,
  handleDisplayButtonClick,
  onDeleteAvatar,
}: AddEditProfileFormProps) => {
  const {
    email,
    firstName,
    lastName,
    nationalProviderIdentifier,
    middleName,
    suffix,
    stateOfResidence,
    languages,
    pronouns,
    credentials,
    phoneNumber,
    providerType,
    timezone,
    otherPronounText,
    capacity,
    hoursPerWeek,
    supervisor,
    displayFirstName,
    displayLastName,
    displayMiddleName,
    displaySuffix,
    pronunciation,
    userStatus,
  } = editForm;

  const TOP_LEVEL_ID = 'profile-add-edit-form';

  const { providerTypePkTransition: isProviderTypePkTransitionEnabled } =
    useFlags<LDFlags>();
  const [inputValue, setInputValue] = useState('');

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const {
    data: providerCredentials,
    isLoading: isGetProviderCredentialsLoading,
  } = useGetProviderCredentialsEnumQuery();
  const { data: providerPronouns, isLoading: isGetProviderPronounsLoading } =
    useGetProviderPronounsEnumQuery();
  const { data: providerLanguages, isLoading: isGetProviderLanguagesLoading } =
    useGetProviderLanguagesEnumQuery();
  const { data: providerStates, isLoading: isGetProviderStatesLoading } =
    useGetProviderStatesEnumQuery();
  const { data: providerTimezones, isLoading: isGetProviderTimezonesLoading } =
    useGetProviderTimezonesEnumQuery();
  const { data: providerCapacity, isLoading: isGetProviderCapacityLoading } =
    useGetProviderCapacityEnumQuery();
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  const { data: ProviderTypes } = useGetProviderTypesEnumQuery();
  const { mode } = useModeContext();
  const isAdminMode = mode === UserMode.ADMIN;

  const avatar_name = `${displayFirstName || firstName} ${
    displayLastName || lastName
  }`;

  const rolesMenuOptions = useMemo<UniversalSelectOption[]>(() => {
    return (ProviderTypes ?? []).map(({ label, id, code }) => ({
      label,
      value: isProviderTypePkTransitionEnabled ? code : id,
    }));
  }, [ProviderTypes, isProviderTypePkTransitionEnabled]);

  const providers = useProvidersSearchData({
    pageSize: 15,
  });

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (supervisorFullName) {
      providers.handleSearch(supervisorFullName, pageNumber);
    }
  }, [supervisorFullName]);

  useEffect(() => {
    setIsImageLoaded(false);
    if (editForm?.avatarSrc) {
      const img = new Image();
      img.onload = () => setIsImageLoaded(true);
      img.src = editForm.avatarSrc;
    }
  }, [editForm?.avatarSrc]);

  const debouncedFetchOptions = useRef(
    debounce((searchTerm: string, pageNumber: number) => {
      providers.handleSearch(searchTerm, pageNumber);
    }, 500),
  ).current;

  const credentialOptions = useMemo<UniversalSelectOption[]>(() => {
    return credentialsEnumMenuOptionsMapper(providerCredentials);
  }, [providerCredentials]);

  const supervisorOptions = React.useMemo(
    () => getMenuOptions(TOP_LEVEL_ID, providers),
    [providers],
  );
  const loadOptions = (inputValue: string) => {
    const newPageNumber = 1;
    debouncedFetchOptions(inputValue, newPageNumber);
    setPageNumber(newPageNumber);
    setInputValue(inputValue);
  };

  const loadMoreOptions = () => {
    if (providers.totalCount === providers.dataList.length) {
      return;
    }
    const newPageNumber = pageNumber + 1;
    debouncedFetchOptions(inputValue, newPageNumber);
    setPageNumber(newPageNumber);
  };

  const handleTrackEvent = () => {
    if (isAddForm && isAdminMode) {
      trackEvent(ProfileEventsAdmin.addUserModalAddDisplayNameButtonClicked, {
        source: AnalyticsSourceValues.PROFILE_TAB_ADMIN,
      });
    } else {
      trackEvent(
        isAdminMode
          ? ProfileEventsAdmin.adminEditUserModalAddDisplayNameButtonClicked
          : ProfileEventsMaud.maudEditUserModalAddDisplayNameButtonClicked,
        {
          source: isAdminMode
            ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
            : AnalyticsSourceValues.PROFILE_TAB_MAUD,
        },
      );
    }
  };

  const isEmailValid = email ? validateEmail(email) : true;

  const languagesOptions = useMemo<UniversalSelectOption[]>(() => {
    return providerLanguagesEnumMenuOptionsMapper(providerLanguages);
  }, [providerLanguages]);
  const stateOptions = useMemo<UniversalSelectOption[]>(() => {
    return providerStatesEnumMenuOptionsMapper(providerStates);
  }, [providerStates]);
  const capacityOptions = useMemo<UniversalSelectOption[]>(() => {
    return providerEnumMenuOptionsMapper(providerCapacity);
  }, [providerCapacity]);
  const timezoneOptions = useMemo<UniversalSelectOption[]>(() => {
    return providerTimezoneEnumMenuOptionsMapper(providerTimezones);
  }, [providerTimezones]);

  const pronounsOptions = useMemo<UniversalSelectOption[]>(() => {
    return providerEnumMenuOptionsMapper(providerPronouns);
  }, [providerPronouns]);

  const isUserActive = userStatus === USER_STATUSES.ACTIVE;
  const hasOtherPronounText = pronouns?.includes('OTHER');

  return (
    <>
      {!isAddForm ? (
        <>
          {isAvatarModalOpen && (
            <AvatarUpload
              isOpen={isAvatarModalOpen}
              onClose={() => setIsAvatarModalOpen(false)}
              providerExternalId={editForm?.userId}
            />
          )}
          <ChakraFlex alignItems="center" gap="24px" justifyContent="center">
            {isUserActive && editForm?.avatarSrc && isImageLoaded && (
              <ChakraBox
                border=" 1px solid"
                borderColor="critical.base"
                borderRadius="100px"
                cursor="pointer"
                id={`${TOP_LEVEL_ID}__avatar-name`}
                onClick={onDeleteAvatar}
                padding="6px"
              >
                <Icon
                  aria-label={FAIconName.trashRegular}
                  color="critical.base"
                  name={FAIconName.trashRegular}
                  size="lg"
                />
              </ChakraBox>
            )}
            <Avatar
              id={`${TOP_LEVEL_ID}__avatar-image`}
              name={avatar_name}
              size="xl"
              src={editForm?.avatarSrc}
              userType="provider"
            />
            {isUserActive && (
              <ChakraBox
                border=" 1px solid"
                borderColor="primary.100"
                borderRadius="100px"
                cursor="pointer"
                id={`${TOP_LEVEL_ID}__avatar-name`}
                onClick={() => setIsAvatarModalOpen(true)}
                padding="6px"
              >
                <Icon
                  aria-label={FAIconName.folderArrowUp}
                  color="primary.100"
                  name={FAIconName.folderArrowUp}
                  size="lg"
                />
              </ChakraBox>
            )}
          </ChakraFlex>
        </>
      ) : (
        <ChakraFlex alignItems="center" justifyContent="center">
          <Avatar
            id={`${TOP_LEVEL_ID}__avatar-image`}
            name={avatar_name}
            size="xl"
            userType="provider"
          />
        </ChakraFlex>
      )}

      <ChakraBox borderRadius="12px" borderWidth="1px" padding="18px">
        <ChakraFlex alignItems="start" flexDirection="column" gap="12px">
          <ChakraFlex alignItems="start" flexDirection="column" gap="2px">
            <Text variant="bodyMedium">Legal name</Text>
            <Text variant="caption">
              This name will be used to record all Maud actions and on note
              signatures. It can only be changed by administrators.
            </Text>
          </ChakraFlex>
          <ChakraSimpleGrid columns={4} spacing="24px" width="100%">
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__legal-first-name`}
              isRequired
              label="First name"
              shouldShowAsteriskWhenRequired
              value={
                isCharacterCountExceeded(firstName, 75) &&
                ERROR_MESSAGES.NAME_LIMIT
              }
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__legal-first-name-input`}
                isDisabled={!isAdminMode}
                isInvalid={
                  isCharacterCountExceeded(firstName, 75) ||
                  requiredFieldErrors.firstName
                }
                onChange={(value) => handleChange('firstName', value)}
                value={firstName}
              />
            </InputGroupV2>
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__legal-middle-name`}
              isRequired={false}
              label="Middle name"
              shouldHideOptionalLabel
              value={
                isCharacterCountExceeded(middleName, 75) &&
                ERROR_MESSAGES.NAME_LIMIT
              }
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__legal-middle-name-input`}
                isDisabled={!isAdminMode}
                isInvalid={isCharacterCountExceeded(middleName, 75)}
                onChange={(value) => handleChange('middleName', value)}
                value={middleName}
              />
            </InputGroupV2>
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__legal-last-name`}
              isRequired
              label="Last name"
              shouldShowAsteriskWhenRequired
              value={
                isCharacterCountExceeded(lastName, 75) &&
                ERROR_MESSAGES.NAME_LIMIT
              }
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__legal-last-name-input`}
                isDisabled={!isAdminMode}
                isInvalid={
                  isCharacterCountExceeded(lastName, 75) ||
                  requiredFieldErrors.lastName
                }
                onChange={(value) => handleChange('lastName', value)}
                value={lastName}
              />
            </InputGroupV2>
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__legal-suffix`}
              isRequired={false}
              label="Suffix"
              shouldHideOptionalLabel
              value={
                isCharacterCountExceeded(suffix, 75) &&
                ERROR_MESSAGES.NAME_LIMIT
              }
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__legal-suffix-input`}
                isDisabled={!isAdminMode}
                isInvalid={isCharacterCountExceeded(suffix, 75)}
                onChange={(value) => handleChange('suffix', value)}
                value={suffix}
              />
            </InputGroupV2>
          </ChakraSimpleGrid>
        </ChakraFlex>
      </ChakraBox>
      {showDisplayName ? (
        <ChakraBox borderRadius="12px" borderWidth="1px" padding="18px">
          <ChakraFlex flexDirection="column" gap="12px">
            <ChakraFlex gap="12px" justifyContent="space-between">
              <ChakraFlex alignItems="start" flexDirection="column" gap="2px">
                <Text variant="bodyMedium">Display name</Text>
                <Text variant="caption">
                  This is how your name will appear throughout Maud and MyEquip.
                </Text>
              </ChakraFlex>

              <FireflyButton
                id={`${TOP_LEVEL_ID}__delete-display-name`}
                leftIcon={FAIconName.trashRegular}
                onClick={() => {
                  handleDisplayButtonClick(false);
                }}
                variant="secondary"
              >
                Delete
              </FireflyButton>
            </ChakraFlex>
            <ChakraSimpleGrid columns={4} spacing="24px" width="100%">
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__display-first-name`}
                isRequired
                label="First name"
                shouldShowAsteriskWhenRequired
                value={
                  isCharacterCountExceeded(displayFirstName, 75) &&
                  ERROR_MESSAGES.NAME_LIMIT
                }
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__display-first-name-input`}
                  isInvalid={
                    isCharacterCountExceeded(displayFirstName, 75) ||
                    requiredFieldErrors?.displayFirstName
                  }
                  onChange={(value) => handleChange('displayFirstName', value)}
                  value={displayFirstName}
                />
              </InputGroupV2>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__display-middle-name`}
                isRequired={false}
                label="Middle name"
                shouldHideOptionalLabel
                value={
                  isCharacterCountExceeded(displayMiddleName, 75) &&
                  ERROR_MESSAGES.NAME_LIMIT
                }
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__display-middle-name-input`}
                  isInvalid={isCharacterCountExceeded(displayMiddleName, 75)}
                  onChange={(value) => handleChange('displayMiddleName', value)}
                  value={displayMiddleName}
                />
              </InputGroupV2>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__display-last-name`}
                isRequired
                label="Last name"
                shouldShowAsteriskWhenRequired
                value={
                  isCharacterCountExceeded(displayLastName, 75) &&
                  ERROR_MESSAGES.NAME_LIMIT
                }
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__display-last-name-input`}
                  isInvalid={
                    isCharacterCountExceeded(displayLastName, 75) ||
                    requiredFieldErrors?.displayLastName
                  }
                  onChange={(value) => handleChange('displayLastName', value)}
                  value={displayLastName}
                />
              </InputGroupV2>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__display-suffix`}
                isRequired={false}
                label="Suffix"
                shouldHideOptionalLabel
                value={
                  isCharacterCountExceeded(displaySuffix, 75) &&
                  ERROR_MESSAGES.NAME_LIMIT
                }
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__display-suffix-input`}
                  isInvalid={isCharacterCountExceeded(displaySuffix, 75)}
                  onChange={(value) => handleChange('displaySuffix', value)}
                  value={displaySuffix}
                />
              </InputGroupV2>
            </ChakraSimpleGrid>
          </ChakraFlex>
        </ChakraBox>
      ) : (
        <ChakraBox>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__delete-display-name`}
            leftIcon={FAIconName.plusSolid}
            onClick={() => {
              handleTrackEvent();
              handleDisplayButtonClick(true);
            }}
            variant="secondary"
          >
            Add display name
          </FireflyButton>
        </ChakraBox>
      )}
      <ChakraBox>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__Pronunciation`}
          isRequired={false}
          label="Pronunciation"
          shouldHideOptionalLabel
          value={
            isCharacterCountExceeded(pronunciation, 100) &&
            ERROR_MESSAGES.PRONUNCIATION_LIMIT
          }
        >
          <TextInputV2
            id={`${TOP_LEVEL_ID}__Pronunciation-input`}
            isInvalid={isCharacterCountExceeded(pronunciation, 100)}
            onChange={(value) => handleChange('pronunciation', value)}
            placeholder="Please share guidance on how to best pronounce your name"
            value={pronunciation}
          />
        </InputGroupV2>
      </ChakraBox>

      <ChakraBox>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__credential`}
          isRequired={false}
          label="Credentials"
          shouldHideOptionalLabel
        >
          <UniversalSelect
            id={`${TOP_LEVEL_ID}__credential-select`}
            isLoading={isGetProviderCredentialsLoading}
            isSingleSelect={false}
            onChange={handleSelectChange('credentials')}
            options={credentialOptions}
            value={getMultiSelectedUniversalSelectOptions(
              credentials,
              credentialOptions,
            )}
            withCheckBox
          />
        </InputGroupV2>
      </ChakraBox>

      <ChakraSimpleGrid columnGap="24px" columns={2} rowGap="18px" width="100%">
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__role`}
          isRequired
          label="Role"
          shouldShowAsteriskWhenRequired
        >
          <UniversalSelect
            id={`${TOP_LEVEL_ID}__role-select`}
            isDisabled={!isAdminMode}
            isInvalid={requiredFieldErrors?.providerType}
            isSingleSelect
            onChange={handleSelectChange('providerType')}
            options={rolesMenuOptions}
            value={getSelectedUniversalSelectOption(
              providerType,
              rolesMenuOptions,
            )}
          />
        </InputGroupV2>
        <ChakraSimpleGrid columns={hasOtherPronounText ? 2 : 1} spacing="24px">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__pronouns`}
            isRequired={false}
            label="Pronouns"
            shouldHideOptionalLabel
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__pronouns-select`}
              isLoading={isGetProviderPronounsLoading}
              isSingleSelect={false}
              onChange={handleSelectChange('pronouns')}
              options={pronounsOptions}
              value={getMultiSelectedUniversalSelectOptions(
                pronouns,
                pronounsOptions,
              )}
              withCheckBox
            />
          </InputGroupV2>
          {hasOtherPronounText && (
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__others-pronouns`}
              isRequired
              label="Other pronouns"
              shouldShowAsteriskWhenRequired
              value={
                isCharacterCountExceeded(otherPronounText, 75) &&
                ERROR_MESSAGES.NAME_LIMIT
              }
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__others-pronouns-select`}
                isInvalid={
                  isCharacterCountExceeded(otherPronounText, 75) ||
                  requiredFieldErrors?.otherPronounText
                }
                onChange={(value) => handleChange('otherPronounText', value)}
                placeholder="Please enter"
                value={otherPronounText}
              />
            </InputGroupV2>
          )}
        </ChakraSimpleGrid>

        <InputGroupV2
          id={`${TOP_LEVEL_ID}__email`}
          isRequired
          label="Email address"
          shouldShowAsteriskWhenRequired
          value={!isEmailValid && 'Please enter a valid email'}
        >
          <TextInputV2
            id={`${TOP_LEVEL_ID}__email-input`}
            isDisabled={!isAdminMode}
            isInvalid={!isEmailValid || requiredFieldErrors?.email}
            leftIcon={FAIconName.envelopeRegular}
            onChange={(value) => handleChange('email', value)}
            type={TextInputV2Type.EMAIL}
            value={email}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__cellphone`}
          isRequired
          label="Cell phone"
          shouldShowAsteriskWhenRequired
          value={
            phoneNumber &&
            !isPhoneNumberValid(phoneNumber) &&
            'Please enter a valid phone number'
          }
        >
          <TextInputV2
            id={`${TOP_LEVEL_ID}__cellphone-input`}
            isInvalid={
              (phoneNumber && !isPhoneNumberValid(phoneNumber)) ||
              requiredFieldErrors?.phoneNumber
            }
            leftIcon={FAIconName.phoneRegular}
            onChange={(value) => handleChange('phoneNumber', value)}
            type={TextInputV2Type.PHONE}
            value={phoneNumber}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__residence`}
          isRequired={false}
          label="State of residence"
          shouldHideOptionalLabel
        >
          <UniversalSelect
            id={`${TOP_LEVEL_ID}__residence-select`}
            isDisabled={!isAdminMode}
            isLoading={isGetProviderStatesLoading}
            isSearchable
            isSingleSelect
            onChange={handleSelectChange('stateOfResidence')}
            options={stateOptions}
            value={getSelectedUniversalSelectOption(
              stateOfResidence,
              stateOptions,
            )}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__timezone`}
          isRequired={false}
          label="Timezone"
          shouldHideOptionalLabel
        >
          <UniversalSelect
            id={`${TOP_LEVEL_ID}__timezone-select`}
            isDisabled={!isAdminMode}
            isLoading={isGetProviderTimezonesLoading}
            isSingleSelect
            onChange={handleSelectChange('timezone')}
            options={timezoneOptions}
            value={getSelectedUniversalSelectOption(timezone, timezoneOptions)}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__Proficient languages`}
          isRequired={false}
          label="Proficient languages"
          shouldHideOptionalLabel
        >
          <UniversalSelect
            id={`${TOP_LEVEL_ID}__languages-select`}
            isLoading={isGetProviderLanguagesLoading}
            isSingleSelect={false}
            onChange={handleSelectChange('languages')}
            options={languagesOptions}
            value={getMultiSelectedUniversalSelectOptions(
              languages,
              languagesOptions,
            )}
            withCheckBox
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__NPI`}
          isRequired={false}
          label="NPI"
          shouldHideOptionalLabel
          value={
            nationalProviderIdentifier &&
            !isNPIValid(nationalProviderIdentifier) &&
            'Please enter a valid NPI'
          }
        >
          <TextInputV2
            characterLimit={10}
            id={`${TOP_LEVEL_ID}__NPI-input`}
            isDisabled={!isAdminMode}
            onChange={(value) =>
              handleChange('nationalProviderIdentifier', value)
            }
            type={TextInputV2Type.NUMBER}
            value={nationalProviderIdentifier}
          />
        </InputGroupV2>

        {isAdminMode && (
          <>
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__supervisor`}
              isRequired={false}
              label="Supervisor"
              shouldHideOptionalLabel
            >
              <UniversalSelect
                id={`${TOP_LEVEL_ID}__supervisor-select`}
                inputValue={inputValue}
                isLoading={providers.isLoading}
                isSearchable
                isSingleSelect
                leftIcon={FAIconName.searchRegular}
                onChange={handleSelectChange('supervisor')}
                onInputChange={loadOptions}
                onMenuScrollToBottom={loadMoreOptions}
                options={supervisorOptions}
                value={getSelectedUniversalSelectOption(
                  supervisor,
                  supervisorOptions,
                )}
                withCheckBox={false}
              />
            </InputGroupV2>
            {capacity === 'PARTTIME' ? (
              <ChakraSimpleGrid columns={2} spacing="24px" width="100%">
                <InputGroupV2
                  id={`${TOP_LEVEL_ID}__capacity`}
                  isRequired
                  label="Capacity"
                  shouldShowAsteriskWhenRequired
                >
                  <UniversalSelect
                    id={`${TOP_LEVEL_ID}____capacity-select`}
                    isInvalid={requiredFieldErrors?.capacity}
                    isLoading={isGetProviderCapacityLoading}
                    isSingleSelect
                    onChange={handleSelectChange('capacity')}
                    options={capacityOptions}
                    value={getSelectedUniversalSelectOption(
                      capacity,
                      capacityOptions,
                    )}
                  />
                </InputGroupV2>
                <InputGroupV2
                  id={`${TOP_LEVEL_ID}__hours-per-week`}
                  label="Hours per week"
                  shouldShowAsteriskWhenRequired
                >
                  <TextInputV2
                    id={`${TOP_LEVEL_ID}____hours-per-week-select`}
                    isInvalid={requiredFieldErrors?.hoursPerWeek}
                    onChange={(value) => {
                      handleChange(
                        'hoursPerWeek',
                        enforceMinMaxBoundsOnHoursPerWeek(String(value)),
                      );
                    }}
                    placeholder="Please enter"
                    type={TextInputV2Type.NUMBER}
                    value={enforceMinMaxBoundsOnHoursPerWeek(
                      String(hoursPerWeek),
                    )}
                  />
                </InputGroupV2>
              </ChakraSimpleGrid>
            ) : (
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__capacity`}
                isRequired
                label="Capacity"
                shouldShowAsteriskWhenRequired
              >
                <UniversalSelect
                  id={`${TOP_LEVEL_ID}____capacity-select`}
                  isInvalid={requiredFieldErrors?.capacity}
                  isLoading={isGetProviderCapacityLoading}
                  isSingleSelect
                  onChange={handleSelectChange('capacity')}
                  options={capacityOptions}
                  value={getSelectedUniversalSelectOption(
                    capacity,
                    capacityOptions,
                  )}
                />
              </InputGroupV2>
            )}
          </>
        )}
      </ChakraSimpleGrid>
    </>
  );
};

export default AddEditProfileForm;
